import { Injectable, NgZone } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Connection } from '../models/websocket';
import { SharedService } from './shared.service';
import { resolveWebsocketUrl } from '../functions';

export interface CollectionChangeEvent {
  collection: string;
  op: 'insert' | 'update' | 'replace' | 'delete' | string;
  id: string;
  company_id: string;
  doc: any | null;
  ts: string;
}

@Injectable({
  providedIn: 'root'
})
export class WebsocketService {
  private socket: Socket | null = null;
  private listenersRegistered = false;

  // Track unread alerts for the notification bell
  private unreadAlertsSubject = new BehaviorSubject<any[]>([]);
  public unreadAlerts$ = this.unreadAlertsSubject.asObservable();

  // Live MongoDB change-stream events for any watched collection.
  // Subscribers should filter by event.collection. See backend/services/change-streams.js.
  public collectionChanges$ = new Subject<CollectionChangeEvent>();

  // Fires when the socket reconnects after being dropped (server restart, network
  // blip, laptop sleep, etc). Any change-stream events broadcast while disconnected
  // are lost — this is the signal to trigger a catch-up resync.
  public reconnected$ = new Subject<void>();

constructor(
  public sharedService: SharedService,
  private ngZone: NgZone
) {
}

  private registerSocketListeners(): void {
    if (!this.socket || this.listenersRegistered) {
      return;
    }

    this.listenersRegistered = true;

    this.socket.on('connect', () => {
      const connection: Connection = new Connection();
      if (this.sharedService.logged_in_user) {
        connection.user_id = this.sharedService.logged_in_user.username;
      }
      if (this.sharedService.company_info) {
        connection.ffl = this.sharedService.company_info.ffl;
        connection.company_id = this.sharedService.company_info.company_id;
      }
      this.socket?.emit('setUserId', connection);
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    // socket.io-client only fires 'reconnect' on the Manager for actual
    // reconnections, not the initial connect.
    this.socket.io.on('reconnect', () => {
      this.ngZone.run(() => this.reconnected$.next());
    });

    // Live MongoDB change-stream events forwarded by the backend.
    this.socket.on('collection_change', (event: CollectionChangeEvent) => {
      this.ngZone.run(() => this.collectionChanges$.next(event));
    });

    // Listen for inventory found notifications
    this.socket.on('message', (payload: any) => {
      this.ngZone.run(() => {
        console.log('Raw payload received:', payload);

        // Unwrap payload when routed through a wrapper
        const messageData = payload.text || payload;

        let parsedMessage = messageData;
        if (typeof messageData === 'string') {
          try {
            parsedMessage = JSON.parse(messageData);
          } catch (e) {}
        }

        if (parsedMessage && parsedMessage.type === 'INVENTORY_FOUND') {
          this.addAlert(parsedMessage.data);
        }
      });
    });
  }

  ensureConnected(): void {
    if (!this.socket) {
      // withCredentials sends the httpOnly session cookie on the handshake so
      // the server can verify the connection belongs to a logged-in user.
      this.socket = io(resolveWebsocketUrl(), { autoConnect: false, withCredentials: true });
      this.registerSocketListeners();
    }

    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  private get alertStorageKey(): string {
    const companyId = this.sharedService.logged_in_user?.company_id || 'anonymous';
    return `unread_inventory_alerts_${companyId}`;
  }

  loadAlertsForCurrentUser(): void {
    const stored = localStorage.getItem(this.alertStorageKey);
    this.unreadAlertsSubject.next(stored ? JSON.parse(stored) : []);
  }

  clearAlertsForPreviousUser(): void {
    this.unreadAlertsSubject.next([]);
  }

  private addAlert(alert: any): void {
    const currentAlerts = this.unreadAlertsSubject.value;
    const existingIndex = currentAlerts.findIndex(a => a.upc === alert.upc);
    
    if (existingIndex >= 0) {
      // Merge with existing alert
      const existing = currentAlerts[existingIndex];
      const vendors = new Set(existing.vendor.split(',').map((v: string) => v.trim()));
      
      if (alert.vendor) {
        alert.vendor.split(',').forEach((v: string) => vendors.add(v.trim()));
      }
      
      existing.vendor = Array.from(vendors).join(', ');
      
      // Update price if lower or new
      if (alert.price !== undefined && (!existing.price || alert.price < existing.price)) {
          existing.price = alert.price;
      }
      // Update qty (we'll just take the latest since Python script already sums it)
      if (alert.qty !== undefined) {
          existing.qty = alert.qty; 
      }
      existing.timestamp = new Date();
      
      // Move to top
      currentAlerts.splice(existingIndex, 1);
      const updatedAlerts = [existing, ...currentAlerts];
      
      localStorage.setItem(this.alertStorageKey, JSON.stringify(updatedAlerts));
      this.unreadAlertsSubject.next(updatedAlerts);
    } else {
      // Add new
      const updatedAlerts = [{ ...alert, timestamp: new Date() }, ...currentAlerts];
      localStorage.setItem(this.alertStorageKey, JSON.stringify(updatedAlerts));
      this.unreadAlertsSubject.next(updatedAlerts);
    }
  }

  public removeAlert(upc: string): void {
    const currentAlerts = this.unreadAlertsSubject.value;
    const updatedAlerts = currentAlerts.filter(a => a.upc !== upc);
    localStorage.setItem(this.alertStorageKey, JSON.stringify(updatedAlerts));
    this.unreadAlertsSubject.next(updatedAlerts);
  }

  public clearAlerts(): void {
    localStorage.setItem(this.alertStorageKey, JSON.stringify([]));
    this.unreadAlertsSubject.next([]);
  }

  sendMessage(msg: any): void {
    this.ensureConnected();
    this.socket?.emit('message', msg);
  }

  getMessages(): Observable<any> {
    return new Observable(observer => {
      this.ensureConnected();
      this.socket?.on('message', (data) => {
        console.error('Message received:', data);
        observer.next(data);
      });
    });
  }

  getInventoryAlerts(): Observable<any> {
    return new Observable(observer => {
      this.ensureConnected();
      this.socket?.on('inventoryAlert', (data) => {
        observer.next(data);
      });
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.listenersRegistered = false;
      this.socket = null;
    }
  }
}
